/* global AutoTrack */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import CoreSalesposter from '@vg/salesposter';
import { config } from '../../config';
import detectAdBlock from '../../utils/adblock-detect';
import styles from '../../../themes/salesposter/styles/default.scss';

import {
    getAppProducts,
    isInAppPurchaseEnabled,
    addPaymentUrl,
    getHeaderConfig,
} from '../../utils/subscription-helper';
import fetchTimeout from '../../utils/fetch-timeout';
import queryParser from '../../utils/query-parser';
import { paywallTypeAction } from '../../redux/actions/paywall-type';

const APP_TARGET = 'app';
const WEB_TARGET = 'web';
const WEB_FALLBACK_TARGET = 'web-fallback';
const USE_HYPERION_POSTER = new RegExp(`^(${APP_TARGET}|${WEB_FALLBACK_TARGET})$`);
const SALEPOSTER_ID = 'salesposter_iframe';

// spid has completed when userId && sig is set, or error (no spid session)
const spidCompleted = ({ sig, userId, error }) => (userId && sig) || error;

// client side feature toggling
const featureToggle = ({ feature, enableThreshold }) => {
    let threshold = enableThreshold;
    const query = queryParser(window.location);
    const p = parseFloat(query[feature]);

    if (!isNaN(p) && p >= 0) {
        threshold = p;
    }

    return Math.random() > 1 - threshold;
};

class Salesposter extends Component {
    static propTypes = {
        articleId: PropTypes.string.isRequired,
        device: PropTypes.string.isRequired,
        dispatch: PropTypes.func.isRequired,
        spid: PropTypes.shape({
            loggedIn: PropTypes.bool,
            login: PropTypes.func,
            userId: PropTypes.number,
            sig: PropTypes.string,
        }),
        loadedTrackers: PropTypes.shape({
            engage: PropTypes.bool,
        }),
        utm: PropTypes.shape({
            content: PropTypes.string,
            source: PropTypes.string,
            medium: PropTypes.string,
            term: PropTypes.string,
            campaign: PropTypes.string,
        }).isRequired,
    };

    static defaultProps = {
        spid: {
            loggedIn: false,
            login: null,
            userId: null,
            sig: null,
        },
        loadedTrackers: {
            engage: false,
        },
    };

    constructor(props) {
        super(props);

        const { salesposter: { products = [] } = {} } = config;

        this.state = {
            target: WEB_TARGET,
            products,
        };
    }

    componentDidMount() {
        const { device } = this.props;
        const { location } = window;
        const {
            salesposter: { products = [], paywallApi: { enableThreshold = 0 } = {} } = {},
        } = config;

        this.articleUrl = `${location.protocol}//${location.host}${location.pathname}`;

        const isAppWithPurchase = device.indexOf(APP_TARGET) > -1 && isInAppPurchaseEnabled();
        detectAdBlock((err) => {
            if (err) {
                this.adblocked = true;
            }
        });

        if (isAppWithPurchase) {
            this.appProducts();
        } else {
            const target = featureToggle({ feature: 'saleposter', enableThreshold })
                ? WEB_TARGET
                : WEB_FALLBACK_TARGET;
            this.setState({ target, products }); // eslint-disable-line react/no-did-mount-set-state

            // Fallback if poster is not loaded
            if (!USE_HYPERION_POSTER.test(this.state.target)) {
                setTimeout(() => {
                    if (!this.loadingPaywall) {
                        this.setState({ target: 'web-fallback' });
                    }
                }, 2000);
            }
        }
    }

    componentWillReceiveProps(nextProps) {
        const {
            spid: { userId, sig, error },
            loadedTrackers,
        } = nextProps;

        if (
            !USE_HYPERION_POSTER.test(this.state.target) &&
            spidCompleted({ sig, userId, error }) &&
            (loadedTrackers.engage || this.adblocked) &&
            !this.loadingPaywall
        ) {
            this.loadingPaywall = true;

            this.webProducts(nextProps);
        }
    }

    appProducts() {
        return this.setState(
            {
                target: APP_TARGET,
                products: getAppProducts(),
            },
            () => this.props.dispatch(paywallTypeAction('iap')),
        );
    }

    async webProducts(props) {
        // Experimental personalised paywall. Implemenation details:
        // https://docs.google.com/presentation/d/1DWtXHbuHzsy5WMI2maLE0ES8ZuOAV2qEiloAQX6fepc/edit?usp=sharing

        const { salesposter: { products = [], paywallApi: { endpoint, timeout } } = {} } = config;
        const { getLoginUrl, userId, sig } = props.spid;

        const baseUrl = this.buyUrlTemplate().replace('{{PRODUCT_ID}}{{CAMPAIGN_ID}}', '');
        const iframeId = SALEPOSTER_ID;
        const pageViewId =
            this.adblocked || !window.AutoTrack ? 'pageViewId' : AutoTrack.getPageViewId();
        const environmentId =
            this.adblocked || !window.AutoTrack ? 'environmentId' : AutoTrack.activity().envId;
        const loginUri = getLoginUrl();

        try {
            const params = [
                `pageViewId=${pageViewId}`,
                `environmentId=${environmentId}`,
                `iframeId=${iframeId}`,
                `baseUrl=${encodeURIComponent(baseUrl)}`,
                `loginUri=${encodeURIComponent(loginUri)}`,
            ].join('&');
            const url = `${endpoint}?${params}`;

            const res = await fetchTimeout(url, {
                headers: {
                    'x-spid-sig': sig,
                    'x-spid-user': userId,
                },
                mode: 'cors',
                timeout,
            });

            const { html } = await res.json();

            const doc = this.iframe.contentDocument;
            // hack: adjust name to set target from links inside iframe.
            window.name = 'top';
            doc.open();
            doc.writeln(html);
            doc.close();
        } catch (err) {
            this.setState({
                target: 'web-fallback',
                products,
            });
        }
    }

    buildUtmParams() {
        const { utm } = this.props;

        return Object.keys(utm)
            .map((param) => `utm_${param}=${this.props.utm[param]}`)
            .join('&');
    }

    buyUrlTemplate() {
        const { articleId } = this.props;
        const { spid: { server: spidUrl, clientId } = {} } = config;

        const utmParams = this.buildUtmParams();
        const utmFragment = utmParams ? `&${utmParams}` : '';

        // Temporary fix for including utm params as part of tag
        const tagUtmParams = utmFragment
            .replace(/&/g, '_')
            .replace(/utm_/g, '')
            .replace(/=/g, '-');

        const urlTemplate =
            this.state.target === APP_TARGET
                ? `vg.no:///buy?articleUrl=${encodeURIComponent(this.articleUrl)}{{APP_PRODUCT_ID}}`
                : `https://${spidUrl}/flow/checkout?response_type=code&flow=payment&client_id=${clientId}` +
                  `&redirect_uri=${encodeURIComponent(
                      this.articleUrl,
                  )}&tag=${articleId}${tagUtmParams}{{PRODUCT_ID}}{{CAMPAIGN_ID}}`;

        return `${urlTemplate}${utmFragment}`;
    }

    render() {
        const {
            salesposter: {
                header = {},
                campaignName = '',
                salespitch = '',
                salespitchContent = '',
            } = {},
        } = config;
        const login = !this.props.spid.loggedIn ? this.props.spid.login : null;

        if (USE_HYPERION_POSTER.test(this.state.target)) {
            return (
                <CoreSalesposter
                    target={this.state.target}
                    spidLogin={login}
                    products={addPaymentUrl(this.state.products, this.buyUrlTemplate())}
                    header={getHeaderConfig(header, this.buyUrlTemplate())}
                    campaignName={campaignName}
                    salespitch={salespitch}
                    salespitchContent={salespitchContent}
                />
            );
        }
        // target === web, title added for accessibility
        return (
            <div>
                <div className={styles.paywallTextFading} />
                <iframe
                    id={SALEPOSTER_ID}
                    title="Kjøp tilgang til VG pluss"
                    frameBorder="0"
                    scrolling="no"
                    width="100%"
                    ref={(iframe) => {
                        this.iframe = iframe;
                    }}
                />
            </div>
        );
    }
}

const enhance = connect((state) => ({
    articleId: state.article.id,
    device: state.device,
    spid: state.spid,
    utm: state.utmParams,
    loadedTrackers: state.loadedTrackers,
}));

export default enhance(Salesposter);



/** WEBPACK FOOTER **
 ** ./src/local-components/article/Salesposter.js
 **/